import { useContext, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, CircularProgress } from '@mui/material';
import { shipmentContext } from '../ShipmentContext';
import * as yup from 'yup';
import { Field, Form, Formik, FormikProps } from 'formik';
import { TextField } from 'formik-mui';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { CircleLoader } from 'components';

interface IEdtiTrackingNumberProps {
    id: string
    shipmentId: string
    trackingNumber: string
}

let EditTrackingNumber = (props: IEdtiTrackingNumberProps) => {

    let { enqueueSnackbar } = useSnackbar();

    let service = useContext(shipmentContext);

    let shipmentTrackingSchema = yup.object({
        id: yup.string().default(props.id ? props.id : ''),
        shipmentId: yup.string().default(props.shipmentId ? props.shipmentId : ''),
        trackingNumber: yup.string().default(props.trackingNumber ? props.trackingNumber : '').required().label('Tracking number')
    });

    type ShipmentTracking = yup.InferType<typeof shipmentTrackingSchema>;


    let [open, setOpen] = useState<boolean>(false);
    let [isLoading, setLoading] = useState<boolean>(false);
    let [isSaving, setSaving] = useState<boolean>(false);

    let [shipmentTrackingData, setShipmentTrackingData] = useState<ShipmentTracking>(shipmentTrackingSchema.getDefault())


    let prepareDialog = async () => {
        setOpen(true);
        setLoading(true);

        try {
            // refresh shipment data (in case it's changed in the meanwhile)
            let res = await axios.get('/api/Shipment/GetShipment', { params: { shipmentId: props.shipmentId } });
            let { item } = res.data;

            if (item)
                setShipmentTrackingData({ ...shipmentTrackingData, trackingNumber: item?.trackingNumber ?? '' });

        } catch (err) { }
        finally {
            setLoading(false);
        }

    }

    let openTrackingUrl = async (formikProps: FormikProps<ShipmentTracking>) => {
        if (!formikProps.values.trackingNumber) {
            window.open(process.env.REACT_APP_GLS_TRACKING_URL, '_blank');
            return;
        }

        let request = {
            shipmentId: props.shipmentId,
            trackingNumber: formikProps.values.trackingNumber
        }

        let res = await axios.post('/api/Shipment/GetTrackingUrl', request);
        let { item } = res.data;

        if (item && item?.trackingUrl)
            window.open(item.trackingUrl, '_blank');
        else
            window.open(process.env.REACT_APP_GLS_TRACKING_URL, '_blank');
    }

    let saveTrackingData = async (values: ShipmentTracking) => {
        try {
            setSaving(true);

            // save data
            await axios.post('/api/Shipment/UpdateTrackingNumber', values);
            enqueueSnackbar("Tracking Number saved", { variant: "success" });

            setSaving(false);

            // refresh shipment
            await service.getShipment();

            setOpen(false);

        } catch (err) {
            setSaving(false);
        }
    }


    return (
        <>
            <Button
                color="primary"
                variant="contained"
                onClick={async () => await prepareDialog()}
            >
                Add tracking number
            </Button>

            <Dialog
                open={open}
                maxWidth="md"
                fullWidth
                onClose={(e, reason) => {
                    if (reason === "escapeKeyDown")
                        setOpen(false)
                }}
            >
                <DialogTitle>Set Tracking Number</DialogTitle>

                <Formik
                    enableReinitialize={true}
                    initialValues={shipmentTrackingData}
                    validationSchema={shipmentTrackingSchema}
                    onSubmit={(values, action) => {
                        saveTrackingData(values);
                        action.setSubmitting(false);;
                    }}
                >
                    {formProps =>
                        <Form>
                            <DialogContent>
                                {isLoading === true && <CircleLoader />}

                                {isLoading === false &&
                                    <Grid container spacing={2}>
                                        <Grid item xs>
                                            <Field
                                                component={TextField}
                                                name='trackingNumber'
                                                fullWidth
                                                size='small'
                                                label='Tracking number'
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                variant="text"
                                                color="primary"
                                                onClick={() => openTrackingUrl(formProps)}
                                            >
                                                Check Tracking number
                                            </Button>
                                        </Grid>
                                    </Grid>
                                }
                            </DialogContent>
                            <DialogActions>
                                <Grid container spacing={2}>
                                    <Grid item xs />
                                    <Grid item>
                                        <Button
                                            variant='outlined'
                                            onClick={() => setOpen(false)}
                                            disabled={isLoading === true || isSaving === true}
                                        >
                                            Close
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={isLoading === true || isSaving === true}
                                        >
                                            {isSaving === true && <><CircularProgress size={24} />&nbsp;&nbsp;</>}
                                            Save
                                        </Button>
                                    </Grid>
                                </Grid>
                            </DialogActions>
                        </Form>
                    }
                </Formik>
            </Dialog>
        </>
    );
}

export default EditTrackingNumber;
